/**
 * Cost Assignment — Phase 13 refactor.
 *
 * Single inline-editable breakdown table:
 *   Personnel cascade → Overhead imputations → Total personnel → Indirect → TOTAL
 * Driver grid above the table for overhead pools + indirect residual.
 * Reconciliation badge in the header.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePersistentValue, usePersistentValueLocal } from '../../hooks/usePersistentState';
import {
  fetchPersonnelCostCascade,
  computeMarkupRatesByPool,
  readCascadeMode,
} from '../../api/tdabc/personnelCostCascade';
import { getChrYearly } from '../../api/chrYearly';
import { getTimeEffective } from '../../api/hrTimeEffective';
import { cmpVal, normalizeName, buildSearchKey } from '../../utils/personTable';
import PageHeader from '../../components/PageHeader';
import InfoTooltip from '../../components/InfoTooltip';
import PersonFilterBar from '../../components/PersonFilterBar';
import SegmentedToggle from '../../components/SegmentedToggle';
import CardShell from '../../components/CardShell';
import SearchField from '../../components/SearchField';
import FilterIcon from '../../components/FilterIcon';
import FilterCount from '../../components/FilterCount';
import PersonName from '../../components/PersonName';
import PersonTh from '../../components/PersonTh';
import { usePersonNameOrder } from '../../hooks/useTableSort';
import './CostAssignmentPage.css';
import './ProfitabilityPage.css';   // réutilise le look (bandeau d'en-tête, colonne TOTAL) des cascades
import { tp, useP } from '../../i18n/useP';

// ── Helpers ───────────────────────────────────────────────────────────────────

function errorMessage(err, fallback) {
  return err?.response?.data?.detail || err?.message || fallback;
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function CostAssignmentPage({ activeTenant, addToast, year: yearProp, onYearChange, setPage = null, pageView = 'indirect', embedded, unit = 'chf', totalSubview = null }) {
  const { t } = useTranslation();
  const p = useP();
  const tenantId = activeTenant?.id;
  const [loading, setLoading]     = useState(false);

  // ── Année (fallback local si non fournie par App) ──
  const [localYear, setLocalYear] = useState(yearProp ?? new Date().getFullYear());
  const year = yearProp ?? localYear;

  // ── Cascade du coût de personnel ──
  // Allocation figée sur la capacité pratique (plus de choix d'inducteur).
  const [cascade, setCascade]         = useState(null);
  // TT (1 EPT) = capacité contractuelle annuelle standard d'un EPT @100%
  // (jours ouvrés × heures/jour). Sert de dénominateur pour l'équivalent EPT.
  const [ttPerFte, setTtPerFte]       = useState(0);

  const load = useCallback(async () => {
    if (!tenantId) { setCascade(null); return; }
    setLoading(true);
    try {
      const cas = await fetchPersonnelCostCascade(tenantId, year);
      setCascade(cas);
      // TT par EPT @100% (t0_company) = dénominateur de l'équivalent EPT de la
      // capacité pratique. Même source que l'écran Temps standard.
      const cty = await getTimeEffective(tenantId, year).catch(() => null);
      setTtPerFte(Number(cty?.data?.t0_company) || 0);
    } catch (e) {
      addToast(errorMessage(e, p('Échec du chargement de l’affectation')), 'error');
    } finally {
      setLoading(false);
    }
  }, [tenantId, year]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  // ── Render guards ─────────────────────────────────────────────────────────

  if (!activeTenant) {
    return (
      <main className="page-table-layout is-fluid is-compact">
        <div className="page-table-empty">{p('Sélectionner une entreprise.')}</div>
      </main>
    );
  }

  if (loading) {
    return (
      <main className="page-table-layout is-fluid is-compact">
        <p className="page-table-loading">{t('common:status.loading')}</p>
      </main>
    );
  }

  if (!cascade) return null;

  const TITLES = {
    total:     p('Coût horaire de capacité (CCR)'),
    personnel: p('Affectation personnel'),
    frais:     p('Affectation frais'),
    indirect:  p('Affectation des charges indirectes'),
  };

  return (
    <main className={`page-table-layout ca-layout is-fluid is-compact${embedded ? ' ca-layout--embedded' : ' ca-layout--standalone'}`}>

      {/* ── En-tête ── (masqué en mode intégré : le hub porte le titre) */}
      {!embedded && <PageHeader title={TITLES[pageView] || TITLES.indirect} />}

      <PersonnelCostCascade
        cascade={cascade}
        tenantId={tenantId}
        year={year}
        onReload={load}
        setPage={setPage}
        addToast={addToast}
        view={pageView}
        ttPerFte={ttPerFte}
        unit={unit}
        totalSubview={totalSubview}
      />

    </main>
  );
}

// ── Personnel cost cascade (MOCK Phase A) ─────────────────────────────────────
// Affiche : pool · Personnel · Absenteism · Corporate · Supervision (CHF)
// + Absent./h · Corp./h · Superv./h calculés selon le mode choisi (within|shared).
// Les toggles bleus permettent de basculer chaque composant indépendamment.
function PersonnelCostCascade({ cascade, tenantId, year, onReload, addToast, setPage = null, view = 'indirect', ttPerFte = 0, unit = 'chf', totalSubview = null }) {
  const p = useP();
const showTotal     = view === 'total';
  // Vue Total scindée pour l'impression : 'synth' = tuiles + card CCR ;
  // 'detail' = table CCR par personne ; null (écran) = les deux.
  /* Une card, deux lectures. `totalSubview` reste honoré s'il est fourni : le
     Coût de production monte cet écran en vue figée. */
  /* Par PERSONNE au repos (2026-09-01) : c'est la lecture qu'on vient chercher,
     le groupe en est l'agrégat. Le choix est RETENU d'une visite à l'autre.
     `totalSubview` reste honoré s'il est fourni : le Coût de production monte
     cet écran en vue figée. */
  const [ccrViewMem, setCcrView] = usePersistentValueLocal(
    `ccr_total_view_${tenantId || ''}`, 'personne');
  const ccrView = totalSubview === 'detail' ? 'personne' : ccrViewMem;
  /* Le PANNEAU sous la bande (proto Olivier du 2026-09-01, après deux essais
     défaits : le remplacement muet du bas d'écran, puis la fenêtre). Une seule
     zone de détail, trois lectures : le coût horaire (défaut), l'affectation
     personnel, l'affectation frais. On y entre par le pied « Voir la
     cascade » des deux tuiles indirectes, ou par la bascule du panneau ; la
     tuile ouverte pointe le panneau d'un caret. */
  const [panel, setPanel] = useState('ccr');    // 'ccr' | 'personnel' | 'frais'
  // 'indirect' = les deux sections ensemble (utilisé par l'impression) ;
  // 'personnel' / 'frais' = un onglet chacun.
  const showPersonnel = view === 'personnel' || view === 'indirect' || (showTotal && panel === 'personnel');
  const showFrais     = view === 'frais'     || view === 'indirect' || (showTotal && panel === 'frais');
  /* En vue Total, l'affectation vit dans le panneau ; ailleurs (onglet dédié,
     impression, export), dans le flux comme avant. */
  const panelToggle = showTotal ? (
    <SegmentedToggle
      compact
      value={panel}
      onChange={setPanel}
      ariaLabel={p('Lecture du panneau')}
      options={[
        { value: 'ccr', label: p('Coût horaire') },
        { value: 'personnel', label: p('Affectation personnel') },
        { value: 'frais', label: p('Affectation frais') },
      ]}
    />
  ) : null;
  /* Entonnoir FANTÔME : ces vues n'ont pas de filtre, mais sans lui la pilule
     glissait à droite en changeant de panneau. Il est là, gris, inactif —
     l'entête ne bouge pas. */
  const ghostFunnel = (
    <button type="button" className="lk-funnel is-ghost" disabled aria-hidden tabIndex={-1}>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8"
           strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M3 4h18l-7 8v6l-4 2v-8L3 4z" />
      </svg>
      {p('Filtrer')}
    </button>
  );
  const wrapDetail = (title, subtitle, node) => (showTotal ? (
    <CardShell className="tcp-card" flush title={title} subtitle={subtitle}
               viewControls={<>{panelToggle}{ghostFunnel}</>}>
      <div className="tcp-panel__body">{node}</div>
    </CardShell>
  ) : node);

  // ── Détail par personne (vue Total cost per pool : tableau CCR par individu) ──
  // LCR individuel (lcr) + mark-up Indirect personnel / Overhead de son pool.
  const [personRows, setPersonRows] = useState([]);
  const [ppSortCol, setPpSortCol]   = usePersistentValue(`tdabc_assign_sort_col_${tenantId}`, 'pcr');
  const [ppSortDir, setPpSortDir]   = usePersistentValue(`tdabc_assign_sort_dir_${tenantId}`, 'desc');
  const { nameOrder, flipNameOrder } = usePersonNameOrder(`tdabc_assign_name_${tenantId}`, ppSortCol, setPpSortCol);
  const [ppPool,    setPpPool]      = usePersistentValue(`tdabc_assign_pool_${tenantId}`, 'all');
  const [ppSearch,  setPpSearch]    = useState('');
  // « Actif » = statut backend (contrat ouvert à ce jour), porté par les lignes CHR.
  const [ppStatus,  setPpStatus]    = usePersistentValue(`tdabc_assign_status_v2_${tenantId}`, 'all');
  useEffect(() => {
    if (!showTotal || !tenantId || !year) { setPersonRows([]); return; }
    let alive = true;
    getChrYearly(tenantId, year)
      .then(r => { if (alive) setPersonRows(r?.data?.rows || []); })
      .catch(() => { if (alive) setPersonRows([]); });
    return () => { alive = false; };
  }, [showTotal, tenantId, year]);
  function ppSort(col) {
    if (ppSortCol === col) setPpSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setPpSortCol(col); setPpSortDir(col === 'first_name_sort' || col === 'last_name_sort' || col === 'pool' ? 'asc' : 'desc'); }
  }
  const fmt2 = v => {
    const n = Number(v);
    if (!Number.isFinite(n) || n === 0) return '0';
    return n.toLocaleString('de-CH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };
  const fmt1 = v => {
    const n = Number(v);
    if (!Number.isFinite(n) || n === 0) return '0';
    return n.toLocaleString('de-CH', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  };
  const fmt0 = v => {
    const n = Number(v);
    if (!Number.isFinite(n) || n === 0) return '0';
    return n.toLocaleString('de-CH', { maximumFractionDigits: 0 });
  };
  // Montant selon l'unité d'affichage : kCHF = ÷ 1000. Les heures (TR…) et les
  // taux CHF/h (LCR, CCR, majorations) restent TOUJOURS en francs.
  const unitLbl = unit === 'kchf' ? 'kCHF' : 'CHF';
  const money = v => {
    const n = Number(v);
    if (!Number.isFinite(n)) return '—';
    return unit === 'kchf' ? fmt0(n / 1000) : fmt0(n);
  };

  // Modes d'allocation (plus d'« heures facturées ») disponibles par composant :
  //  - within   : Spécifique au groupe (coût gardé dans le groupe)
  //  - shared   : Capacité pratique (mutualisé sur Σ TR)
  //  - excluded : Exclure des affectations (0 réaffectation) — congés spéciaux seul
  // Coûts globaux (corporate, indirect_costs) : Capacité pratique uniquement.
  // ── Calculs d'allocation par pool — clés FIXES (plus de choix) ────────────
  const tot = cascade.totals;
  const pools = cascade.pools || [];
  const totalHrs = tot.production_hours || 0;

  // Allocate(component) selon la clé fixe :
  //  - 'within' : spécifique au groupe (coût gardé dans son groupe)
  //  - 'shared' : capacité pratique (mutualisé sur Σ TR)
  function alloc(component, mode, pool) {
    if (mode === 'within') return pool[component] || 0;
    const totalAmt = tot[component] || 0;
    return totalHrs > 0 ? (totalAmt / totalHrs) * (pool.production_hours || 0) : 0;
  }
  // Indirect costs (résidu) : alloué sur la capacité pratique (mode global unique).
  // Le RÉSIDU = CI totale saisie − Σ Specific indirect costs (saisis directement
  // sur un groupe, ils réduisent la base à répartir).
  const totIndirectGross    = tot.indirect_costs    || 0;
  const totSpecificIndirect = tot.specific_indirect || 0;
  const totIndirectResidual = Math.max(0, totIndirectGross - totSpecificIndirect);

  function allocIndirectResidual(pool) {
    return totalHrs > 0 ? (totIndirectResidual / totalHrs) * (pool.production_hours || 0) : 0;
  }

  // Précalculs par pool — congés spéciaux exclus du périmètre analytique
  // (déchargés de la base ET non réaffectés) ; indemnités perçues ignorées.
  const computed = pools.map(pool => {
    const productive       = pool.productive_labour || 0;
    const personnel        = pool.personnel || 0;                     // charges de personnel (salaire chargé)
    const conges           = pool.absenteism || 0;                    // congés spéciaux (brut) — exclus
    const chargesAllouer   = personnel - conges;                   // base à allouer
    const corp             = alloc('corporate',   'shared', pool);   // gestion → capacité pratique
    const sup              = alloc('supervision', 'within', pool);   // supervision → spécifique groupe
    const exp              = alloc('expenses',    'shared', pool);
    const specificIndirect = pool.specific_indirect || 0;             // saisie directe par groupe
    const indirectResid    = allocIndirectResidual(pool);             // résidu réparti
    const indirect         = specificIndirect + indirectResid;
    const subPers    = productive + corp + sup;                    // masse salariale affectée (hors congés)
    const subExt     = exp + indirect;                             // externes (indemnités ignorées)
    const total      = subPers + subExt;
    const tr         = pool.production_hours || 0;
    return { pool, productive, personnel, conges, chargesAllouer, corp, sup, exp, specificIndirect, indirectResid, indirect, subPers, subExt, total, tr };
  });

  // Totaux
  const totProductive     = tot.productive_labour || 0;
  const totPersonnel      = tot.personnel         || 0;
  const totConges         = tot.absenteism        || 0;   // congés spéciaux (exclus)
  const totChargesAllouer = totPersonnel - totConges;
  const totCorp           = tot.corporate         || 0;
  const totSup            = tot.supervision       || 0;
  const totExp            = tot.expenses          || 0;
  const totIndirect       = tot.indirect_costs    || 0;
  const totSubPers        = totProductive + totCorp + totSup;   // = charges à allouer (réconciliation)
  const totSubExt         = totExp + totIndirect;
  const totTotal          = totSubPers + totSubExt;
  const totQph            = totalHrs > 0 ? totTotal / totalHrs : null;

  // Helper /h global pour une ligne
  const perH = (totalAmt) => (totalHrs > 0 ? totalAmt / totalHrs : 0);

  // Helper "cell" : montant ou — si 0/null
  const cell = (v) => (v === null ? '—' : money(v));
  const cellNum = (v) => (v === null ? '—' : fmt2(v));

  // Headers de pools
  const nbPools = pools.length;
  // Cols = label(1) + costDriver(1) + TOTAL(1) + pools(N) + /h global(1)
  const totalCols = 3 + nbPools + 1;

  // Header de table factorisé — répété au-dessus de chacun des 3 blocs.
  // Le titre de section remplace l'en-tête « COMPOSANT » (1 col).
  // Le driver est intégré dans la cellule COMPOSANT (à droite du label).
  function CascadeHead({ pools, title }) {
    return (
      <>
        {/* Colgroup identique sur les 3 tables → colonnes alignées entre blocs. */}
        <colgroup>
          <col className="ca-cpv-col-label" />
          <col className="ca-cpv-col-total" />
          {pools.map(pool => <col key={pool.pool_id} className="ca-cpv-col-pool" />)}
        </colgroup>
        <thead>
          <tr>
            <th className="ca-cpv-th-label ca-cpv-th-section">{title}</th>
            <th className="ca-cpv-th-total">TOTAL</th>
            {pools.map(pool => (
              <th key={pool.pool_id} className={`ca-cpv-th-pool${pool.is_unassigned ? ' ca-cpv-th-unassigned' : ''}`} title={pool.pool_name}>
                {pool.pool_name}
              </th>
            ))}
          </tr>
        </thead>
      </>
    );
  }

  // Helper : rend une ligne avec ordre fixe (Label · CostDriver · TOTAL · Pools… · /h global)
  // - driverNode : toggle ou null (cellule vide si null)
  // - valueByPool est un tableau aligné sur `pools` ; vh est le /h global (ou null).
  function Row({ rowClass, labelNode, driverNode = null, totalValue, valueByPool, fmt = cell }) {
    return (
      <tr className={rowClass || undefined}>
        <td className="ca-cpv-label">
          <span className="ca-cpv-label-wrap">{labelNode}</span>
          {driverNode && <span className="ca-cpv-driver-inline">{driverNode}</span>}
        </td>
        <td className="ca-td-num ca-cpv-total">{fmt(totalValue)}</td>
        {valueByPool.map((v, i) => (
          <td key={i} className="ca-td-num">{fmt(v)}</td>
        ))}
      </tr>
    );
  }

  // ── "Hypothetical indirect cost" — base costing hypothétique ──────────────
  // Source UNIQUE du montant indirect qui pilote le CCR/markup : table
  // tdabc_hypothetical_indirect (tenant × année). DÉCOUPLÉE du phasing cash de
  // Planning → Indirect cost. Elle se SAISIT dans la section « Charges
  // indirectes », qui la détaille poste par poste ; ici on la lit.

  // Cellule input pour la saisie d'un Specific indirect cost par pool.
  // Click = édition ; blur/Enter = upsert + reload cascade.
  /* Une saisie se lit et s'écrit dans l'UNITÉ AFFICHÉE. Les deux champs de
     cet écran restaient en francs quelle que soit la bascule : on lisait 50
     dans la colonne Total et 50'000 dans la cellule juste à côté, et taper
     « 50 » en kCHF enregistrait cinquante francs (2026-08-23). */
  const toU = (v) => (unit === 'kchf' ? (Number(v) || 0) / 1000 : (Number(v) || 0));
  const fromU = (v) => (unit === 'kchf' ? v * 1000 : v);
  const draftOf = (v) => String(unit === 'kchf' ? Math.round(toU(v) * 10) / 10 : Math.round(v || 0));

  /* La charge indirecte ne se saisit PLUS ici (2026-08-30) : elle est le total
     de la section « Charges indirectes », qui la détaille poste par poste. Deux
     endroits pour écrire un même chiffre, c'est un jour où ils divergent. Le
     lien mène à la saisie. */

  // Règle d'allocation des temps indirects (clés fixes) — affichée en tête de
  // l'onglet Affectation personnel, à la place de l'ancien choix d'inducteur.
  const personnelParamCard = (
    <div className="ca-rule-card">
      <p className="ca-rule-title">{p("Règle d'allocation des temps indirects")}</p>
      <ul className="ca-rule-list">
        <li><b>{p('Congés spéciaux')}</b>{p(' — exclus du périmètre analytique (ni capacité, ni charge).')}</li>
        <li><b>{p('Gestion entreprise')}</b>{p(' — répartie sur la capacité pratique (Σ TR).')}</li>
        <li><b>{p('Supervision production')}</b>{p(' — spécifique au groupe (gardée dans son groupe).')}</li>
      </ul>
    </div>
  );

  // Affectation frais : charge indirecte hypothétique (pilote le CCR, découplée
  // du cash de Planning) + rappel de la clé d'allocation (fixe).
  const fraisParamCard = (
    <div className="ca-param-card ca-param-card--compact">
      <div className="ca-param-row ca-param-row--cost">
        <span className="ca-param-label">{p('Charges indirectes')} <small>{unitLbl}</small></span>
        <span className="ca-param-static">{money(tot.indirect_costs || 0)}</span>
        {setPage && (
          <button type="button" className="ca-param-link"
                  onClick={() => setPage('indirect-items')}>
            {p('Modifier dans Charges indirectes')} ›
          </button>
        )}
      </div>
    </div>
  );

  // Cost build-up : on exclut les pools overhead (coûts redistribués vers les
  // pools productifs). Totaux du pied recalculés sur les pools affichés.
  const cbRows = computed.filter(c => !c.pool.is_overhead_pool);
  const cbSum = (sel) => cbRows.reduce((s, c) => s + (Number(sel(c)) || 0), 0);

  // ── Données pour la vue Total cost per pool (KPI + 3 cards) ──
  const dirPctG = totTotal > 0 ? totProductive / totTotal * 100 : 0;

  // ── Total cost per pool : 3 cards (CCR · cost structure · time usage) ──
  const tcpRows = cbRows.map(c => {
    const direct = c.productive || 0;
    const indPers = c.subPers - c.productive;
    const overhead = (c.specificIndirect || 0) + (c.indirectResid || 0) + (c.exp || 0);
    const total = c.total || 0;
    const tr = c.tr || 0;
    return {
      name: c.pool.pool_name, total, tr, direct, indPers, overhead,
      pcr: tr > 0 ? total / tr : 0,
      lcrU: tr > 0 ? direct / tr : 0,
      indPersU: tr > 0 ? indPers / tr : 0,
      ovhU: tr > 0 ? overhead / tr : 0,
    };
  });
  const tcpMaxPcr   = Math.max(1, ...tcpRows.map(r => r.pcr));
  const totIndPers  = totSubPers - totProductive;
  const totOverhead = totSpecificIndirect + totIndirectResidual + totExp;
  /* Équivalent EPT de la capacité pratique — corrigé le 2026-08-30, comme la
     tuile « EPT de capacité ». La base était le TT d'un EPT (heures du contrat,
     congés compris) alors que le numérateur, Σ TR, en avait déjà retiré les
     congés : les vacances comptaient DEUX FOIS. La base est le TEMPS OUVERT
     d'un EPT, obtenu de l'effectif total (Σ TO ÷ TO d'un EPT). */
  const otPerFte = (tot.fte || 0) > 0 ? (tot.working_hours || 0) / tot.fte : 0;
  const capacityFte = otPerFte > 0 ? totalHrs / otPerFte : 0;
  // Moyenne entreprise (pondérée par TR) — barre affichée en tête de la card CCR.
  const tcpTrSum = tcpRows.reduce((s, r) => s + r.tr, 0);
  const tcpAvg = {
    name: p('Moyenne entreprise'),
    lcrU:     tcpTrSum > 0 ? tcpRows.reduce((s, r) => s + r.direct,   0) / tcpTrSum : 0,
    indPersU: tcpTrSum > 0 ? tcpRows.reduce((s, r) => s + r.indPers,  0) / tcpTrSum : 0,
    ovhU:     tcpTrSum > 0 ? tcpRows.reduce((s, r) => s + r.overhead, 0) / tcpTrSum : 0,
    pcr:      tcpTrSum > 0 ? tcpRows.reduce((s, r) => s + r.total,    0) / tcpTrSum : 0,
  };
  const kchf = (v) => `${Math.round((Number(v) || 0) / 1000)}k`;
  const pctw = (v, max) => `${max > 0 ? (v / max * 100).toFixed(1) : 0}%`;

  // ── CCR par personne (tableau détail individuel) ──
  // LCR individuel (lcr) + mark-up du pool : Indirect personnel + Overhead.
  const markupByPool = computeMarkupRatesByPool(cascade, readCascadeMode(tenantId));
  // Exclure les personnes des pools overhead (coûts redistribués, comme cbRows).
  const overheadPoolIds = new Set(
    computed.filter(c => c.pool.is_overhead_pool).map(c => c.pool.pool_id),
  );
  const ppRowsAll = personRows
    .filter(person => !overheadPoolIds.has(person.cost_pool_id))
    .map(person => {
    const lcr = Number(person.lcr) || 0;
    const mk = (person.cost_pool_id && markupByPool[person.cost_pool_id]) || null;
    const indPers = mk ? mk.indirect_personnel : 0;
    const overhead = mk ? mk.overhead : 0;
    const pcr = lcr + indPers + overhead;
    const tr = Number(person.heures_prod) || 0;
    const ot = Number(person.heures_contractuelles) || 0;
    return {
      id: person.person_id,
      first_name: person.first_name,
      last_name: person.last_name,
      first_name_sort: normalizeName(person.first_name),
      last_name_sort: normalizeName(person.last_name),
      _search: buildSearchKey(person.first_name, person.last_name, person.department, person.cost_pool_name),
      status: person.status || null,
      pool: person.cost_pool_name || '—',
      poolId: person.cost_pool_id || '__none__',
      lcr, indPers, overhead, pcr, tr, ot, total: pcr * tr,
    };
  });
  // Options de filtre (pools présents) + lignes filtrées (pool + statut + recherche).
  const ppPoolOpts = Array.from(
    ppRowsAll.reduce((m, r) => m.set(r.poolId, r.pool), new Map()).entries(),
  );
  const ppQ = normalizeName(ppSearch.trim());
  const ppFiltered = ppRowsAll.filter(r => {
    if (ppPool !== 'all' && r.poolId !== ppPool) return false;
    if (ppStatus === 'active' && !(r.status === 'active' || r.status === 'joined')) return false;
    if (ppQ && !r._search.includes(ppQ)) return false;
    return true;
  });
  const ppRows = [...ppFiltered].sort((a, b) => {
    const c = cmpVal(a[ppSortCol], b[ppSortCol]);
    return ppSortDir === 'asc' ? c : -c;
  });
  // Totaux / moyennes : taux pondérés (LCR par OT ; mark-up & CCR par TR).
  const ppSum = (sel) => ppFiltered.reduce((s, r) => s + (Number(sel(r)) || 0), 0);
  const ppTrSum = ppSum(r => r.tr);
  const ppOtSum = ppSum(r => r.ot);
  const ppFoot = {
    lcr:      ppOtSum > 0 ? ppSum(r => r.lcr * r.ot) / ppOtSum : 0,
    indPers:  ppTrSum > 0 ? ppSum(r => r.indPers * r.tr) / ppTrSum : 0,
    overhead: ppTrSum > 0 ? ppSum(r => r.overhead * r.tr) / ppTrSum : 0,
    pcr:      ppTrSum > 0 ? ppSum(r => r.total) / ppTrSum : 0,
    tr:       ppTrSum,
    total:    ppSum(r => r.total),
  };
  /* Capacité valorisée SUR TOUT LE PÉRIMÈTRE, filtres ignorés (2026-08-26).
     Le contrôle du pied de card vérifie une identité du MODÈLE : le coût
     construit par le haut doit égaler celui consommé par le bas. Il lisait le
     total du pied de table, lequel ne somme que les lignes visibles — dès qu'on
     filtrait sur les actifs, un sortant sortait de la somme et l'écran annonçait
     un écart de plusieurs centaines de milliers de francs alors que rien
     n'était faux. Le pied de TABLE reste filtré : il décrit ce qu'on voit. */
  const ppAllTotal = ppRowsAll.reduce((s, r) => s + (Number(r.total) || 0), 0);
  const PP_COLS = [
    { key: 'person',   label: p('Personne'),                  align: 'left',  fmt: null, personSort: true },
    { key: 'pool',     label: p('Groupe ressources'),         align: 'left',  fmt: null  },
    { key: 'lcr',      label: p('Salaire horaire (LCR)'),     align: 'right', fmt: fmt2  },
    { key: 'indPers',  label: p('Personnel indirect'),        align: 'right', fmt: fmt2  },
    { key: 'overhead', label: tp('phrases:p_eb0ea9791d'),            align: 'right', fmt: fmt2  },
    { key: 'pcr',      label: tp('phrases:p_b9fa3a5448'),        align: 'right', fmt: fmt2, accent: true },
    { key: 'tr',       label: tp('phrases:p_35b83cce91'),    align: 'right', fmt: fmt0  },
    { key: 'total',    label: tp('phrases:p_ab3b49387e'),          align: 'right', fmt: money, strong: true },
  ];

  // ── Cascades (style Pool profitability) : colonnes = TOTAL + tous les pools ──
  const icols = computed;                 // Admin inclus (il se décharge)
  const n2    = icols.length + 2;
  const pSum  = (sel) => icols.reduce((s, c) => s + (Number(sel(c)) || 0), 0);
  // Une ligne de cascade (label · TOTAL · 1 cellule par pool).
  function MRow({ label, total, vals, strong, indent, neg, sub, markup }) {
    const w = (x) => ((strong || markup) ? <strong>{x}</strong> : x);
    const ncls = neg ? ' ca-cas-neg' : '';
    return (
      <tr className={`prof-row-stat${markup ? ' ca-cas-markup' : ''}`}>
        <td className={`prof-td-metric${indent ? ' prof-td-metric--indent30' : ''}${ncls}`}>
          {w(label)}{sub && <span className="prof-tier-hint"> {sub}</span>}
        </td>
        <td className={`prof-td-num prof-td-total${ncls}`}>{w(total)}</td>
        {vals.map((v, i) => <td key={i} className={`prof-td-num prof-td-stat${ncls}`}>{w(v)}</td>)}
      </tr>
    );
  }
  const CascHead = ({ title }) => (
    <thead>
      <tr className="prof-thead-block"><th className="prof-th-block-label" colSpan={n2}>{title}</th></tr>
      <tr>
        <th className="prof-th-metric"></th>
        <th className="prof-th-pool prof-th-total">Total</th>
        {icols.map(c => <th key={c.pool.pool_id} className="prof-th-pool">{c.pool.pool_name}</th>)}
      </tr>
    </thead>
  );
  const CascCols = () => (
    <colgroup>
      <col className="prof-col-metric" />
      <col className="prof-col-total" />
      {icols.map(c => <col key={c.pool.pool_id} className="prof-col-pool" />)}
    </colgroup>
  );

  /* ── Données des visuels CCR (2026-08-21) ────────────────────────────
     Trois lectures avant les tableaux : l'entonnoir du temps, la montée du
     coût, et la répartition à 100 % de chaque source indirecte. Rien n'est
     recalculé — on rassemble les totaux déjà établis par la cascade. */

  return (
    <>
      {/* ── Total cost per pool : KPI + 3 cards ── */}
      {/* Les trois panneaux visuels ont quitté cet écran le 2026-08-23 : le
          récit « Résumé » porte l'entonnoir du temps et la construction du
          coût, en plus lisible et en plus complet ; la répartition à 100 %
          faisait doublon avec la cascade juste en dessous. */}
      {showTotal && (
        /* L'ÉQUATION du CCR, en tuiles (2026-09-01) : travail productif +
           personnel indirect + frais généraux = coût total ; ÷ capacité
           pratique = CCR moyen. Mêmes noms que la cascade. Trois tuiles sont
           des BOUTONS : le chiffre est la porte de son détail. */
        <section className="hr-synth hr-synth--flat" style={{ marginBottom: 14 }}>
          <div className="hr-synth__head">{p('Synthèse coûts & capacité · tous les groupes')}</div>
          <div className="tcp-synth-tiles tcp-synth-tiles--eq">
            <div className="tcp-tile">
              <div className="tcp-tile__label">{p('Travail productif')}</div>
              <div className="tcp-tile__value">{money(totProductive)} <small>{unitLbl}</small></div>
              <div className="tcp-tile__rate">{totalHrs > 0 ? fmt2(totProductive / totalHrs) : '—'} <small>/h</small></div>
              <div className="tcp-tile__hint">{p('LCR moyen')}</div>
            </div>
            <span className="tcp-eq__op" aria-hidden>+</span>
            <div className={`tcp-tile tcp-tile--pick${panel === 'personnel' ? ' is-open' : ''}`}>
              <div className="tcp-tile__label">{p('Personnel indirect')}</div>
              <div className="tcp-tile__value">{money(totIndPers)} <small>{unitLbl}</small></div>
              <div className="tcp-tile__rate">{totalHrs > 0 ? fmt2(totIndPers / totalHrs) : '—'} <small>/h</small></div>
              <div className="tcp-tile__hint">{p('majoration moyenne')}</div>
              <button type="button" className="tcp-tile__foot"
                      onClick={() => setPanel(panel === 'personnel' ? 'ccr' : 'personnel')}>
                {panel === 'personnel' ? <>{p('Fermer')} <span aria-hidden>⌃</span></> : <>{p('Voir la cascade')} <span aria-hidden>⌄</span></>}
              </button>
            </div>
            <span className="tcp-eq__op" aria-hidden>+</span>
            <div className={`tcp-tile tcp-tile--pick${panel === 'frais' ? ' is-open' : ''}`}>
              <div className="tcp-tile__label">{tp('phrases:p_eb0ea9791d')}</div>
              <div className="tcp-tile__value">{money(totOverhead)} <small>{unitLbl}</small></div>
              <div className="tcp-tile__rate">{totalHrs > 0 ? fmt2(totOverhead / totalHrs) : '—'} <small>/h</small></div>
              <div className="tcp-tile__hint">{p('majoration moyenne')}</div>
              <button type="button" className="tcp-tile__foot"
                      onClick={() => setPanel(panel === 'frais' ? 'ccr' : 'frais')}>
                {panel === 'frais' ? <>{p('Fermer')} <span aria-hidden>⌃</span></> : <>{p('Voir la cascade')} <span aria-hidden>⌄</span></>}
              </button>
            </div>
            <span className="tcp-eq__op" aria-hidden>=</span>
            <div className="tcp-tile">
              <div className="tcp-tile__label">{tp('phrases:p_004b64cd2b')}</div>
              <div className="tcp-tile__value">{money(totTotal)} <small>{unitLbl}</small></div>
              <div className="tcp-tile__hint">{p('tous les groupes')}</div>
            </div>
            <span className="tcp-eq__op" aria-hidden>÷</span>
            <div className="tcp-tile">
              <div className="tcp-tile__label">{p('Heures disponibles')}</div>
              <div className="tcp-tile__value">{fmt0(totalHrs)} <small>h</small></div>
              <div className="tcp-tile__hint">{p('capacité pratique (TR)')} · {fmt1(capacityFte)} EPT</div>
            </div>
            <span className="tcp-eq__op" aria-hidden>=</span>
            <div className="tcp-tile tcp-tile--result">
              <div className="tcp-tile__label">{p('CCR moyen')}</div>
              <div className="tcp-tile__value">{totQph != null ? fmt2(totQph) : '—'} <small>/h</small></div>
              <div className="tcp-tile__hint">{p('coût d’une heure disponible')}</div>
            </div>
          </div>
        </section>
      )}

      {/* UNE card, DEUX lectures (2026-08-23) : le coût horaire par groupe et
          le même par personne disaient le même sujet dans deux cards empilées,
          si bien qu'il fallait faire défiler pour comparer. Une bascule, un
          seul cadre — au standard de carte depuis le 2026-09-01 : la vue dans
          l'entête, les filtres sous l'entonnoir. */}
      {showTotal && panel === 'ccr' && (
        <CardShell
          className="tcp-card"
          flush
          title={<>{p('Coût horaire de capacité')}<InfoTooltip term="ccr" /></>}
          subtitle={ccrView === 'groupe' ? p('(CHF par heure de capacité pratique TR)') : `CHF/h · Total ${unitLbl}`}
          viewControls={(
            /* Ordre FIXE (2026-09-01) : la vue Par personne/groupe se glisse
               DEVANT la pilule des trois lectures — ainsi la pilule ne bouge
               pas quand on change de panneau, et l'entonnoir ferme la ligne. */
            <>
              <SegmentedToggle
                compact
                label={p('Vue')}
                value={ccrView}
                onChange={setCcrView}
                options={[
                  { value: 'personne', label: p('Par personne') },
                  { value: 'groupe', label: p('Par groupe') },
                ]}
              />
              {panelToggle}
            </>
          )}
          filters={ccrView === 'personne' ? (
            <>
              <FilterIcon />
              <SegmentedToggle
                compact
                label={p('Statut')} value={ppStatus} onChange={setPpStatus}
                options={[
                  { value: 'all', label: p('Tous') },
                  { value: 'active', label: p('Actifs'), tone: 'alert' },
                ]}
              />
              <select className="lk-filter-select" value={ppPool} onChange={e => setPpPool(e.target.value)}>
                <option value="all">{p('Tous les groupes')}</option>
                {ppPoolOpts.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
              </select>
              <FilterCount
                count={(ppStatus === 'active' ? 1 : 0) + (ppPool !== 'all' ? 1 : 0) + (ppSearch.trim() ? 1 : 0)}
                onClear={() => { setPpStatus('all'); setPpPool('all'); setPpSearch(''); }}
              />
            </>
          ) : null}
          search={ccrView === 'personne'
            ? <SearchField value={ppSearch} onChange={setPpSearch} placeholder={p('Personne ou groupe…')} />
            : null}
          filterCount={ccrView === 'personne'
            ? (ppStatus === 'active' ? 1 : 0) + (ppPool !== 'all' ? 1 : 0) + (ppSearch.trim() ? 1 : 0)
            : 0}
          collapsible storageKey="ccr-total"
        >
          {ccrView === 'groupe' ? (<>
          <div className="tcp-leg">
            <span><i className="tcp-sw tcp-direct" />{p('Salaire horaire (LCR)')}</span>
            <span><i className="tcp-sw tcp-indpers" />{p('Majoration personnel indirect')}</span>
            <span><i className="tcp-sw tcp-ovh" />{p('Majoration frais généraux')}</span>
          </div>
          {[tcpAvg, ...tcpRows].map((r, i) => (
            <div className={`tcp-row${i === 0 ? ' tcp-row--avg' : ''}`} key={i}>
              <span className="tcp-name">{r.name}</span>
              <span className="tcp-barcell"><span className="tcp-bar" style={{ width: pctw(r.pcr, tcpMaxPcr) }}>
                <span className="tcp-seg tcp-direct" style={{ width: pctw(r.lcrU, r.pcr) }}>{fmt1(r.lcrU)}</span>
                <span className="tcp-seg tcp-indpers" style={{ width: pctw(r.indPersU, r.pcr) }}>{fmt1(r.indPersU)}</span>
                <span className="tcp-seg tcp-ovh" style={{ width: pctw(r.ovhU, r.pcr) }}>{fmt1(r.ovhU)}</span>
              </span></span>
              <span className="tcp-right">CCR <b className="tcp-pcr">{fmt1(r.pcr)}</b>/h</span>
            </div>
          ))}
          </>) : (<>
          <div className="tcp-ptable-wrap">
            <table className="tcp-ptable">
              <thead>
                <tr>
                  {PP_COLS.map(c => {
                    if (c.personSort) {
                      return (
                        <PersonTh
                          key={c.key}
                          sortCol={ppSortCol} sortDir={ppSortDir} onSort={ppSort}
                          nameOrder={nameOrder} onFlip={flipNameOrder}
                        />
                      );
                    }
                    return (
                      <th key={c.key}
                          className={`${c.align === 'right' ? 'is-num' : ''}${ppSortCol === c.key ? ' is-sorted' : ''}${c.accent ? ' tcp-col-ccr' : ''}`}
                          onClick={() => ppSort(c.key)}
                          title={p('Cliquer pour trier')}>
                        {c.label} <span className="tcp-sort-ind">{ppSortCol === c.key ? (ppSortDir === 'asc' ? '↑' : '↓') : '↕'}</span>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {ppRows.map(r => (
                  <tr key={r.id}>
                    {PP_COLS.map(c => (
                      <td key={c.key} className={`${c.align === 'right' ? 'is-num' : ''}${c.accent ? ' tcp-pcr tcp-col-ccr' : ''}${ppSortCol === c.key ? ' is-sortedcol' : ''}`}>
                        {c.personSort
                          ? <PersonName firstName={r.first_name} lastName={r.last_name} order={nameOrder} />
                          : c.fmt ? (c.strong ? <strong>{c.fmt(r[c.key])}</strong> : c.fmt(r[c.key])) : r[c.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td>{p('Total / Moyenne · ')}{ppRows.length}{p(' personnes')}</td>
                  <td>—</td>
                  <td className="is-num"><em>{fmt2(ppFoot.lcr)}</em></td>
                  <td className="is-num"><em>{fmt2(ppFoot.indPers)}</em></td>
                  <td className="is-num"><em>{fmt2(ppFoot.overhead)}</em></td>
                  <td className="is-num tcp-pcr"><strong>{fmt2(ppFoot.pcr)}</strong></td>
                  <td className="is-num"><strong>{fmt0(ppFoot.tr)}</strong></td>
                  <td className="is-num"><strong>{money(ppFoot.total)}</strong></td>
                </tr>
              </tfoot>
            </table>
          </div>
          {/* CONTRÔLE. Le coût total se construit par le haut (charges de
              personnel, congés spéciaux retirés, frais) et se consomme par le
              bas (chaque personne porte ses heures de capacité, valorisées à
              son coût horaire). Les deux chemins doivent aboutir au même
              franc. On n'affiche pas un écart nul comme un succès : on le dit
              quand il existe, et l'écran reste sobre sinon.
              Il porte sur le PÉRIMÈTRE ENTIER, jamais sur la sélection
              affichée : filtrer une table ne change pas le modèle, et un
              contrôle qui suivrait le filtre crierait à l'erreur au premier
              tri par statut. */}
          {(() => {
            const gap = ppAllTotal - totTotal;
            const ok = Math.abs(gap) < 1;
            return (
              <div className={`tcp-check${ok ? ' is-ok' : ' is-off'}`}>
                <span>{p('Capacité pratique valorisée')} <b>{money(ppAllTotal)}</b></span>
                <span>{p('Coût total')} <b>{money(totTotal)}</b></span>
                <span className="tcp-check__gap">
                  {p('Écart')} <b>{ok ? '—' : `${gap > 0 ? '+' : '−'}${money(Math.abs(gap))}`}</b>
                </span>
              </div>
            );
          })()}
          </>)}
        </CardShell>
      )}

      {/* ── Affectation personnel : règle + 1 card (temps TO→TR puis coûts) ── */}
      {showPersonnel && wrapDetail(p('Affectation personnel'), p('Cascade du coût de personnel') + ' · ' + unitLbl, <>
      {personnelParamCard}

      <section className="prof-section">
        <div className="prof-section-body">
          {/* Temps : de l'opérationnel à la capacité pratique */}
          <div className="prof-table-wrap">
            <table className="prof-table prof-output-table">
              <CascCols />
              <CascHead title={tp('phrases:p_31bdcc9cd5')} />
              <tbody>
                <MRow strong label={tp('phrases:p_dcf6e0e541')} total={fmt0(tot.working_hours)} vals={icols.map(c => fmt0(c.pool.working_hours))} />
                <MRow indent label={tp('phrases:p_1c3958a7af')} total={fmt0(pSum(c => c.pool.absence_hours))} vals={icols.map(c => fmt0(c.pool.absence_hours))} />
                <MRow indent label={p("− Gestion entreprise")} total={fmt0(pSum(c => c.pool.corporate_hours))} vals={icols.map(c => fmt0(c.pool.corporate_hours))} />
                <MRow indent label={p("− Supervision production")} total={fmt0(pSum(c => c.pool.supervision_hours))} vals={icols.map(c => fmt0(c.pool.supervision_hours))} />
                <MRow strong label={p("= Capacité pratique (TR)")} total={fmt0(tot.production_hours)} vals={icols.map(c => fmt0(c.pool.production_hours))} />
              </tbody>
            </table>
          </div>

          {/* Coûts : même card → colonnes alignées sur le tableau des temps */}
          <div className="prof-table-wrap ca-table-gap">
            <table className="prof-table prof-output-table">
              <CascCols />
              <CascHead title={tp('phrases:p_72a379e606')} />
              <tbody>
                <MRow strong label={p("Charges de personnel")} total={cell(totPersonnel)} vals={icols.map(c => cell(c.personnel))} />
                <MRow neg label={tp('phrases:p_157c7e8a39')} total={`−${money(totConges)}`} vals={icols.map(c => (c.conges ? `−${money(c.conges)}` : cell(0)))} />
                <MRow strong label={p("= Charges de personnel à allouer")} total={cell(totChargesAllouer)} vals={icols.map(c => cell(c.chargesAllouer))} />
                <MRow neg label={tp('phrases:p_086e3d357a')} total={`−${money(totChargesAllouer - totProductive)}`} vals={icols.map(c => `−${money(c.chargesAllouer - c.productive)}`)} />
                <MRow strong label={p("= Travail productif (LCR × TR)")} total={cell(totProductive)} vals={icols.map(c => cell(c.productive))} />
                <MRow indent label={tp('phrases:p_e98eb7869c')} total={cell(totCorp)} vals={icols.map(c => cell(c.corp))} />
                <MRow indent label={tp('phrases:p_e347435a35')} total={cell(totSup)} vals={icols.map(c => cell(c.sup))} />
                <MRow strong label={p("= Coût total du personnel")} total={cell(totSubPers)} vals={icols.map(c => cell(c.subPers))} />
                <MRow label={tp('phrases:p_338788f988')} total={cell(totSubPers - totProductive)} vals={icols.map(c => cell(c.subPers - c.productive))} />
                <MRow markup label={p("Majoration personnel indirect (÷ TR)")} total={totalHrs > 0 ? fmt2((totSubPers - totProductive) / totalHrs) : '—'} vals={icols.map(c => (c.tr > 0 ? fmt2((c.subPers - c.productive) / c.tr) : '—'))} />
              </tbody>
            </table>
          </div>
        </div>
      </section>
      </>)}

      {/* ── Affectation frais : charge indirecte hypothétique + autres coûts ── */}
      {showFrais && wrapDetail(p('Affectation frais'), p('Charges indirectes et frais alloués') + ' · ' + unitLbl, <>
      {fraisParamCard}
      <section className="prof-section">
        <div className="prof-section-body">
          <div className="prof-table-wrap">
            <table className="prof-table prof-output-table">
              <CascCols />
              <CascHead title={tp('phrases:p_d2fc22d9dc')} />
              <tbody>
                <tr className="prof-row-stat">
                  <td className="prof-td-metric">{p('Charges spécifiques')}</td>
                  <td className="prof-td-num prof-td-total">{cell(totSpecificIndirect)}</td>
                  {icols.map(c => (
                    <td key={c.pool.pool_id} className="prof-td-num prof-td-stat">
                      {/* Plus de saisie ici (2026-08-30) : une charge
                          spécifique est la PART d'une ligne de charge
                          indirecte, elle se pose sur cette ligne, dans la
                          section « Charges indirectes ». Deux endroits pour
                          découper le même montant, c'est un jour où ils
                          divergent. */}
                      {c.pool.is_overhead_pool
                        ? <span className="prof-td-muted" title={tp('phrases:p_7a5dd57fa1')}>🔒</span>
                        : cell(c.specificIndirect)}
                    </td>
                  ))}
                </tr>
                <MRow label={p("Solde des charges indirectes")} total={cell(totIndirectResidual)} vals={icols.map(c => cell(c.indirectResid))} />
                <MRow indent label={p("+ Frais")} total={cell(totExp)} vals={icols.map(c => cell(c.exp))} />
                <MRow strong label={p("= Total autres coûts")} total={cell(totSpecificIndirect + totIndirectResidual + totExp)} vals={icols.map(c => cell(c.specificIndirect + c.indirectResid + c.exp))} />
                <MRow markup label={tp('phrases:p_cae174c071')} total={totalHrs > 0 ? fmt2((totSpecificIndirect + totIndirectResidual + totExp) / totalHrs) : '—'} vals={icols.map(c => (c.tr > 0 ? fmt2((c.specificIndirect + c.indirectResid + c.exp) / c.tr) : '—'))} />
              </tbody>
            </table>
          </div>
        </div>
      </section>
      </>)}

    </>
  );
}
